import React, { useState, useEffect } from "react";
import { IoMdAdd } from "react-icons/io";
import UserListTable from "./UserList/UserList";
import { IoAdd } from "react-icons/io5";
import { MdClose } from "react-icons/md";
import PhoneInput from "react-phone-input-2";
import { Form, useFormik } from "formik";
import * as Yup from "yup";
import {
  getApiCaller,
  postFormDataApiCaller,
  deleteApiCaller,
} from "../../../../Libs/apiCaller";
import { toast } from "react-toastify";
import { generateRSAKeyPair } from "../../../../utils/cryptoUtils";
import NewProfileModal from "../../../SetupProfile/AboutUserProfile/NewProfileModal/NewProfileModal";

const statuses = [
  "Love my self",
  "Busy",
  "Available",
  "At office",
  "In a meeting",
  "Working remotely",
  "On a break",
  "Out of the office",
  "Traveling for business",
  "In a training session",
  "On vacation",
  "Sick leave",
  "Pending response",
  "In a conference",
];

export default function User() {
  const [imgError, setImgError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [imagePreview, setImagePreview] = useState(null); // For image upload
  const [imagefile, setImagefile] = useState(null);
  const [userList, setUserList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [search, setSearch] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newUserDetails, setNewUserDetails] = useState({
    name: "",
    email: "",
    phone: "",
    countryCode: "",
    gender: "",
    avatar: "",
    about: "",
    username: "",
    password: "",
  });
  const [isModalLoading, setIsModalLoading] = useState(false);
  // Define validation schema
  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Name is required")
      .min(2, "Name must be at least 2 characters")
      .test("no-spaces", "Name is required", (value) => {
        return value && value.trim().length > 0;
      }),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required")
      .test("no-spaces", "Email is required", (value) => {
        return value && value.trim().length > 0;
      }),
    phone: Yup.string()
      .required("Phone number is required")
      .min(10, "Phone number must be 10 digits long")
      .max(15, "Phone number is too long")
      .test("no-spaces", "Phone number is required", (value) => {
        return value && value.trim().length > 0;
      }),
    countryCode: Yup.string()
      .required("Country code is required")
      .test("no-spaces", "Country code is required", (value) => {
        return value && value.trim().length > 0;
      }),
    gender: Yup.string()
      .oneOf(["Male", "Female", "Other"], "Invalid gender")
      .required("Gender is required")
      .test("no-spaces", "Gender is required", (value) => {
        return value && value.trim().length > 0;
      }),
    about: Yup.string()
      .required("About is required")
      .test("no-spaces", "About is required", (value) => {
        return value && value.trim().length > 0;
      }),
    ...(!selectedUser && {
      username: Yup.string()
        .required("Username is required")
        .test("no-spaces", "Username is required", (value) => {
          return value && value.trim().length > 0;
        })
        .test("is-valid-username", "Invalid username", function (value) {
          const usernameRegex = /^[a-zA-Z0-9_.-]{3,}$/;
          return usernameRegex.test(value);
        }),
      password: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
        .matches(/[a-z]/, "Password must contain at least one lowercase letter")
        .matches(/[0-9]/, "Password must contain at least one number")
        .matches(
          /[!@#$%^&*(),.?":{}|<>]/,
          "Password must contain at least one special character"
        )
        .required("Password is required"),
    }),
  });

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImagePreview(URL.createObjectURL(file));
      setImagefile(file);
      setImgError("");
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setImgError("Profile image is required");
  };

  useEffect(() => {
    setIsLoading(true);
    getUserList(1, search, selectedTime);
  }, [selectedTime]);

  useEffect(() => {
    setImgError("");
    if (selectedUser) {
      setNewUserDetails({
        name: selectedUser.name,
        email: selectedUser.email,
        phone: selectedUser.phone,
        countryCode: selectedUser.countryCode,
        gender: selectedUser.gender,
        avatar: selectedUser.userImage || selectedUser.avtarImage,
        about: selectedUser.about,
      });
      setImagePreview(selectedUser.userImage || selectedUser.avtarImage);
    } else {
      setNewUserDetails({
        name: "",
        email: "",
        phone: "",
        gender: "",
        avatar: null,
        username: "",
        password: "",
      });
      setImagePreview(null);
    }
  }, [selectedUser, isAddUserModalOpen]);

  const getUserList = async (
    page = 1,
    srch = search,
    selTime = selectedTime
  ) => {
    let endPoint = `users/all_user?page=${page}&limit=10&search=${srch}`;
    if (selTime) endPoint += `&sortType=${selTime}`;
    const response = await getApiCaller(endPoint);
    if (response.status === 200) {
      window.scrollTo(0, 0);
      setSearch(srch);
      setCurrentPage(response.data.currentPage);
      setUserList(response.data.users);
      setTotalUsers(response.data.totalUsers);
      setTotalPages(response.data.totalPages);
    }
    setIsLoading(false);
  };

  const onSubmit = async (values) => {
    if (imgError) {
      return;
    }
    if (!imagePreview) {
      setImgError("Profile image is required");
      return;
    }
    setImgError("");
    setIsModalLoading(true);
    if (selectedUser) {
      await editUser(values);
    } else {
      await addUser(values);
    }
  };

  const formik = useFormik({
    initialValues: newUserDetails,
    validationSchema: validationSchema,
    onSubmit: onSubmit,
  });

  useEffect(() => {
    if (selectedUser) {
      formik.setValues({
        name: selectedUser.name,
        email: selectedUser.email,
        phone: selectedUser.phone,
        countryCode: selectedUser.countryCode,
        gender: selectedUser.gender,
        avatar: selectedUser.userImage || selectedUser.avtarImage,
        about: selectedUser.about,
      });
    } else {
      formik.resetForm();
    }
  }, [selectedUser, isAddUserModalOpen]);

  const editUser = async (values) => {
    const endpoint = `users/admin_update_user`;
    const formData = new FormData();
    formData.append("userID", selectedUser._id);
    formData.append("name", values.name);
    formData.append("email", values.email);
    formData.append("country_code", values.countryCode.replace("+", ""));
    formData.append("phone", values.phone);
    formData.append("gender", values.gender);
    formData.append("about", values.about);
    if (selectedUser.avatar != imagePreview && imagePreview != null) {
      formData.append("UserMedia", imagePreview);
    }
    const response = await postFormDataApiCaller(endpoint, formData);
    if (response.status === 200) {
      setUserList((prev) =>
        prev.map((user) =>
          user._id === selectedUser._id ? { ...user, ...values } : user
        )
      );
      toast.success("User updated successfully");

      // Reset modal and form
      closeAddUserModal();
    } else {
      toast.error(response.data.message);
    }
    setIsModalLoading(false);
  };

  const addUser = async (values) => {
    const { publicKey, privateKey } = await generateRSAKeyPair();
    const formData = new FormData();
    formData.append("name", values.name);
    formData.append("phone", values.phone);
    formData.append("countryCode", values.countryCode.replace("+", ""));
    formData.append("role", "User");
    formData.append("email", values.email);
    formData.append("password", values.password);
    formData.append("userName", values.username);
    formData.append("gender", values.gender);
    formData.append("about", values.about);
    formData.append("publicKey", publicKey);
    formData.append("encryptedPrivateKey", privateKey);
    formData.append("UserMedia", imagefile);
    const endPoint = "auth/admin_add_user";
    const response = await postFormDataApiCaller(endPoint, formData);
    if (response?.status === 200) {
      setIsLoading(true);
      getUserList();
      toast.success("User added successfully");
      closeAddUserModal();
    } else {
      toast.error(response?.response?.data?.message || response.message);
    }
    setIsModalLoading(false);
  };

  const openAddUserModal = () => {
    setIsAddUserModalOpen(true);
  };

  const closeAddUserModal = () => {
    setIsAddUserModalOpen(false);
    setSelectedUser(null);
  };

  const deleteUser = async () => {
    if (!selectedUser) {
      toast.error("User not found");
      return false;
    }
    const endpoint = `users/${selectedUser._id}`;
    const response = await deleteApiCaller(endpoint);
    if (response.status === 200) {
      toast.success("User deleted successfully");
      setUserList((prev) =>
        prev.filter((user) => user._id !== selectedUser._id)
      );
      setSelectedUser(null);
      return true;
    } else {
      toast.error(response?.response?.data?.message || response.message);
      return false;
    }
  };

  return (
    <section className="p-2">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-black">Users List</h1>
        <button
          onClick={() => {
            openAddUserModal();
            setSelectedUser(null);
          }}
          className="bg-[var(--primary-color)] text-md text-white rounded-full px-4 py-2.5 flex items-center gap-2 font-medium hover:opacity-60"
        >
          <IoMdAdd />
          Add User
        </button>
      </div>
      {isAddUserModalOpen && (
        <div className={`modal ${isAddUserModalOpen ? "modal-open" : ""}`}>
          <div className="modal-box max-w-3xl bg-white">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">
                {selectedUser ? "Edit User" : "Add User"}
              </h2>
              <MdClose
                className="cursor-pointer"
                onClick={closeAddUserModal}
                size={20}
              />
            </div>
            <form onSubmit={formik.handleSubmit} autoComplete="false">
              {/* Profile Image Section */}
              <div className="profile-bg max-w-xs flex flex-col mx-auto">
                <div className="my-4 w-[130px] h-[130px] mx-auto rounded-4xl border border-dashed border-[#000] overflow-hidden relative">
                  <label
                    htmlFor="imageUpload"
                    className="bg-white cursor-pointer w-full h-full flex items-center justify-center"
                  >
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Profile Preview"
                        className="w-full h-full object-cover rounded-4xl"
                      />
                    ) : (
                      <IoAdd className="text-[#7104F8] text-4xl" />
                    )}
                  </label>

                  {imagePreview && (
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-1 right-1 bg-[var(--primary-color)] rounded-full p-1 shadow"
                    >
                      <MdClose className="text-white text-xl" />
                    </button>
                  )}

                  <input
                    type="file"
                    id="imageUpload"
                    accept=".jpg,.png"
                    value=""
                    onChange={(e) => {
                      handleImageChange(e);
                      formik.setFieldValue("avatar", e.target.files[0]);
                    }}
                    className="hidden"
                  />
                </div>
                {imgError && (
                  <div className="text-red-500 text-sm mx-auto w-max">
                    {imgError}
                  </div>
                )}
              </div>
              {/* Upload Info Section */}
              <div className="text-center pt-2">
                <p className="text-md text-gray-400 font-normal my-3 w-2/4 text-center flex mx-auto justify-center">
                  Upload a Photo of the User. Click the Submit Button to
                  Continue.
                </p>
              </div>

              {/* User Info Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                {/* User Name Field */}
                <div className="mb-0">
                  <label
                    htmlFor="userName"
                    className="block text-sm text-gray-700 pb-2"
                  >
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="userName"
                    name="name"
                    className="w-full p-2.5 border border-[#EBEBEB] rounded-md"
                    placeholder="Enter Full Name"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.name}
                  />
                  {formik.errors.name && formik.touched.name ? (
                    <div className="text-red-500 text-sm">
                      {formik.errors.name}
                    </div>
                  ) : null}
                </div>

                {/* Phone Number Field */}
                <div className="mb-0">
                  <label
                    htmlFor="phoneNumber"
                    className="block text-sm text-gray-700 pb-2"
                  >
                    Phone Number
                  </label>
                  <div className="flex">
                    <PhoneInput
                      disabled={selectedUser ? true : false}
                      country={"us"}
                      value={formik.values.countryCode + formik.values.phone}
                      onChange={(phone, data) => {
                        const dialCode = data?.dialCode || "";
                        const number = phone.slice(dialCode.length);

                        formik.setFieldValue("countryCode", `+${dialCode}`);
                        formik.setFieldValue("phone", number);
                      }}
                      containerClass="!w-full !border !rounded-md"
                      inputProps={{
                        placeholder: "Enter phone number",
                        name: "phone",
                        autoFocus: false,
                      }}
                      inputClass="!w-full !py-5.5 !pl-14 !pr-4 !rounded-lg !border-none !border-[#EBEBEB] !focus:ring-2 !focus:ring-purple-500 !focus:border-purple-500 !text-base"
                      buttonClass="!bg-transparent !border-none !outline-none !px-3"
                      dropdownClass="!z-50 border-left"
                      inputStyle={{ width: "100%" }}
                    />
                  </div>
                  {formik.errors.phone && formik.touched.phone ? (
                    <div className="text-red-500 text-sm">
                      {formik.errors.phone}
                    </div>
                  ) : null}
                </div>

                {/* Username Field (New User Only) */}
                {!selectedUser && (
                  <div className="mb-0">
                    <label
                      htmlFor="username"
                      className="block text-sm text-gray-700 pb-2"
                    >
                      Username
                    </label>
                    <input
                      type="text"
                      id="username"
                      name="username"
                      className="w-full p-2.5 border border-[#EBEBEB] rounded-md"
                      placeholder="Enter Username"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.username}
                    />
                    {formik.errors.username && formik.touched.username ? (
                      <div className="text-red-500 text-sm">
                        {formik.errors.username}
                      </div>
                    ) : null}
                  </div>
                )}

                {/* Password Field (New User Only) */}
                {!selectedUser && (
                  <div className="mb-0">
                    <label
                      htmlFor="password"
                      className="block text-sm text-gray-700 pb-2"
                    >
                      Password
                    </label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      className="w-full p-2.5 border border-[#EBEBEB] rounded-md"
                      placeholder="Enter Password"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.password}
                    />
                    {formik.errors.password && formik.touched.password ? (
                      <div className="text-red-500 text-sm">
                        {formik.errors.password}
                      </div>
                    ) : null}
                  </div>
                )}

                {/* Email Field */}
                <div className="mb-0">
                  <label
                    htmlFor="email"
                    className="block text-sm text-gray-700 pb-2"
                  >
                    Email
                  </label>
                  <input
                    disabled={selectedUser ? true : false}
                    type="email"
                    id="email"
                    name="email"
                    className="w-full p-2.5 border border-[#EBEBEB] rounded-md"
                    placeholder="Enter Email Address"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                  />
                  {formik.errors.email && formik.touched.email ? (
                    <div className="text-red-500 text-sm">
                      {formik.errors.email}
                    </div>
                  ) : null}
                </div>

                {/* Gender Field */}
                <div className="mb-0">
                  <label
                    htmlFor="gender"
                    className="block text-sm text-gray-700 pb-2"
                  >
                    Gender
                  </label>
                  <select
                    id="gender"
                    name="gender"
                    className="w-full p-2.5 border border-[#EBEBEB] rounded-md"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.gender}
                  >
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                  {formik.errors.gender && formik.touched.gender ? (
                    <div className="text-red-500 text-sm">
                      {formik.errors.gender}
                    </div>
                  ) : null}
                </div>

                {/* About Field */}
                <div className="mb-4 md:col-span-2">
                  <label className="block mb-1 font-normal">About</label>
                  <div
                    onClick={() => setIsModalOpen(true)}
                    className="select bg-white select-bordered rounded-lg h-12 w-full flex items-center px-3 cursor-pointer"
                  >
                    {formik.values.about || "Enter About You"}
                  </div>
                  {formik.errors.about && formik.touched.about && (
                    <p className="text-red-500 text-sm mt-1 errorMsg">
                      {formik.errors.about}
                    </p>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className="w-full text-sm flex justify-end gap-3 items-center mt-6">
                <button
                  type="button"
                  className="bg-transparent text-black border border-[#ebebe] py-2 px-6 rounded-full hover:opacity-50"
                  onClick={closeAddUserModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  onClick={() => {
                    if (!imagePreview) {
                      setImgError("Profile image is required");
                      return;
                    } else {
                      setImgError("");
                    }
                  }}
                  disabled={isModalLoading}
                  className="bg-[#7104F8] text-white border border-[#7104F8] py-2 px-8 rounded-full hover:opacity-50"
                >
                  {isModalLoading ? "Loading..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <NewProfileModal
        isOpen={isModalOpen}
        onSave={(selectedAbout) => {
          formik.setFieldValue("about", selectedAbout);
          setIsModalOpen(false);
        }}
        isAbout={selectedUser?.about || ""}
        onClose={() => setIsModalOpen(false)}
      />
      <UserListTable
        selectedTime={selectedTime}
        setSelectedTime={setSelectedTime}
        openAddUserModal={openAddUserModal}
        setSelectedUser={setSelectedUser}
        userList={userList}
        setUserList={setUserList}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        getUserList={getUserList}
        currentPage={currentPage}
        totalPages={totalPages}
        totalUsers={totalUsers}
        search={search}
        setSearch={setSearch}
        deleteUser={deleteUser}
      />
    </section>
  );
}
